import React, { useState } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import { Alert, Button, Card, Checkbox, Input, Space, Typography, message } from 'antd';

import adminAxios from './adminAxios';
import './AdminMaintenanceTools.css';

const API_BASE = resolveFrontendApiBase();

const { Paragraph, Text, Title } = Typography;

const sanitizeFilenamePart = (value: string): string =>
  String(value || '')
    .trim()
    .replace(/[^a-z0-9._-]+/gi, '-')
    .replace(/^-+|-+$/g, '') || 'report';

const downloadJson = (payload: unknown, filename: string): void => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

type ExportKind = 'translation' | 'proofread' | 'combined';

const AdminStageTraceCostExportTab: React.FC = () => {
  const [bookId, setBookId] = useState('');
  const [includeRaw, setIncludeRaw] = useState(false);
  const [loadingKind, setLoadingKind] = useState<ExportKind | null>(null);

  const trimmedBookId = bookId.trim();
  const exportDisabled = !trimmedBookId;

  const runExport = async (kind: ExportKind): Promise<void> => {
    const params: Record<string, boolean | number> = { include_raw: includeRaw };
    let endpoint = '';

    if (kind === 'translation') {
      endpoint = `${API_BASE}/api/admin/books/${encodeURIComponent(trimmedBookId)}/translation-stage-trace-cost-export-all`;
      params.limit_per_chapter = 5000;
    } else if (kind === 'proofread') {
      endpoint = `${API_BASE}/api/admin/books/${encodeURIComponent(trimmedBookId)}/proofread-stage-trace-cost-export-all`;
    } else {
      endpoint = `${API_BASE}/api/admin/books/${encodeURIComponent(trimmedBookId)}/combined-stage-trace-cost-export-all`;
      params.limit_per_chapter = 5000;
    }

    const filename = [
      'stage-trace-costs',
      sanitizeFilenamePart(trimmedBookId),
      sanitizeFilenamePart(kind),
      'all-language-pairs',
      new Date().toISOString().slice(0, 10),
    ]
      .filter(Boolean)
      .join('_') + '.json';

    try {
      setLoadingKind(kind);
      const { data } = await adminAxios.get(endpoint, { params });
      downloadJson(data, filename);
      message.success(`${kind[0].toUpperCase()}${kind.slice(1)} report downloaded.`);
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        `Failed to export ${kind} stage trace report.`;
      message.error(String(detail));
    } finally {
      setLoadingKind(null);
    }
  };

  return (
    <Card className="admin-maintenance-tool">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div className="admin-maintenance-tool__header">
          <Title level={4} style={{ marginBottom: 8 }}>
            Stage Trace Cost Export
          </Title>
          <Paragraph className="admin-maintenance-tool__purpose" style={{ marginBottom: 0 }}>
            Download per-stage cost reports for one book. Combined export includes both translation and proofread rows,
            and each row includes <Text code>pipelineType</Text> so you can separate them later.
          </Paragraph>
        </div>

        <Space wrap size="middle" className="admin-maintenance-tool__controls admin-maintenance-tool__section">
          <Input
            placeholder="Book ID"
            value={bookId}
            onChange={(event) => setBookId(event.target.value)}
            style={{ width: 220 }}
          />
        </Space>

        <Alert
          className="admin-maintenance-tool__effect"
          type="info"
          showIcon
          message="Book-wide Export"
          description="Only Book ID is required. The backend scans all translated chapter collections under that book, includes original-language proofreads, and writes sourceLanguage, targetLanguage, and languagePair into each exported row."
        />

        <div className="admin-maintenance-tool__section">
          <Checkbox checked={includeRaw} onChange={(event) => setIncludeRaw(event.target.checked)}>
            Include raw stage event payloads
          </Checkbox>
        </div>

        <Space wrap className="admin-maintenance-tool__controls admin-maintenance-tool__actions">
          <Button
            type="primary"
            onClick={() => void runExport('translation')}
            loading={loadingKind === 'translation'}
            disabled={exportDisabled}
          >
            Download Translation Report
          </Button>
          <Button
            onClick={() => void runExport('proofread')}
            loading={loadingKind === 'proofread'}
            disabled={exportDisabled}
          >
            Download Proofread Report
          </Button>
          <Button
            onClick={() => void runExport('combined')}
            loading={loadingKind === 'combined'}
            disabled={exportDisabled}
          >
            Download Combined Report
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

export default AdminStageTraceCostExportTab;
